export class Moreless {
  constructor(block) {
    this._block = block;
    this._content = block.querySelector('[data-moreless-content]');
    this._btn = block.querySelector('[data-moreless-btn]');
    const btnText = this._btn ? this._btn.querySelector('[data-moreless-btn-text]') : null;
    this._btnText = btnText || this._btn;

    this._itemsLimit = Number(block.dataset.morelessItems) || 0;
    this._linesLimit = Number(block.dataset.morelessLines) || 0;
    this._heightLimit = Number(block.dataset.morelessHeight) || 0;
    this._destroyRule = block.dataset.morelessDestroy || '';
    this._originalText = this._btnText ? this._btnText.textContent.trim() : '';
    this._isDestroyed = false;
    this._destroyConfig = null;
    this._isOpen = false;
    this._collapsedHeight = 0;
    this._isPinning = false;
    this._anchorTop = 0;

    this._onResize = this._onResize.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onLoad = this._onLoad.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);

    this._parseDestroyRule();
  }

  init() {
    if (!this._content || !this._btn) {
      return;
    }

    this._content.style.overflow = 'hidden';
    this._content.style.overflowAnchor = 'none';
    this._content.style.transition = 'max-height 0.4s ease';
    this._block.style.overflowAnchor = 'none';

    this._update(false);
    this._btn.addEventListener('click', this._onClick);
    window.addEventListener('resize', this._onResize);

    if (document.readyState !== 'complete') {
      window.addEventListener('load', this._onLoad);
    }
  }

  destroy() {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('load', this._onLoad);

    if (this._btn) {
      this._btn.removeEventListener('click', this._onClick);
    }

    this._reset();
  }

  toggle() {
    if (this._isDestroyed) {
      return;
    }

    this._isOpen = !this._isOpen;
    this._applyState(true);
  }

  open() {
    if (this._isDestroyed || this._isOpen) {
      return;
    }

    this._isOpen = true;
    this._applyState(true);
  }

  close() {
    if (this._isDestroyed || !this._isOpen) {
      return;
    }

    this._isOpen = false;
    this._applyState(true);
  }

  refresh() {
    this._update(false);
  }

  isOpen() {
    return this._isOpen;
  }

  isDestroyed() {
    return this._isDestroyed;
  }

  _getItems() {
    const children = Array.from(this._content.children);

    if (children.length === 1) {
      const child = children[0];
      if (child.tagName === 'UL' || child.tagName === 'OL') {
        return Array.from(child.children);
      }
    }

    return children;
  }

  _parseDestroyRule() {
    if (!this._destroyRule) {
      return;
    }

    const match = this._destroyRule.match(/\((max-width|min-width):\s*(\d+)px\)/);

    if (!match) {
      return;
    }

    this._destroyConfig = {
      type: match[1],
      value: parseInt(match[2], 10),
    };
  }

  _shouldDestroy() {
    if (!this._destroyConfig) {
      return false;
    }

    if (this._destroyConfig.type === 'min-width') {
      return window.innerWidth <= this._destroyConfig.value;
    }

    if (this._destroyConfig.type === 'max-width') {
      return window.innerWidth >= this._destroyConfig.value;
    }

    return false;
  }

  _reset() {
    if (this._content) {
      this._content.style.maxHeight = '';
      this._content.style.overflow = '';
      this._content.style.transition = '';
      this._content.classList.remove('is-open');
    }

    if (this._btn) {
      this._btn.style.display = '';
      this._btn.classList.remove('is-active');
    }

    if (this._btnText) {
      this._btnText.textContent = this._originalText;
    }

    this._isOpen = false;
    this._stopPinning();
  }

  _getHeightToItem(item) {
    const contentRect = this._content.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const itemStyles = getComputedStyle(item);
    const contentStyles = getComputedStyle(this._content);
    const marginBottom = parseFloat(itemStyles.marginBottom) || 0;
    const paddingBottom = parseFloat(contentStyles.paddingBottom) || 0;

    return Math.ceil(itemRect.bottom - contentRect.top + marginBottom + paddingBottom);
  }

  _getLineHeight() {
    const probe = this._content.querySelector('p, li, span, div') || this._content;
    const styles = getComputedStyle(probe);
    const lineHeight = parseFloat(styles.lineHeight);

    if (!isNaN(lineHeight) && lineHeight > 0) {
      return lineHeight;
    }

    const fontSize = parseFloat(styles.fontSize) || 16;
    return fontSize * 1.4;
  }

  _getHeightByLines(lines) {
    const lineHeight = this._getLineHeight();
    const styles = getComputedStyle(this._content);
    const padding =
      (parseFloat(styles.paddingTop) || 0) +
      (parseFloat(styles.paddingBottom) || 0);

    return Math.round(Math.max(1, lines) * lineHeight + padding);
  }

  _snapHeightToLines(height) {
    const lineHeight = this._getLineHeight();
    const styles = getComputedStyle(this._content);
    const padding =
      (parseFloat(styles.paddingTop) || 0) +
      (parseFloat(styles.paddingBottom) || 0);
    const available = Math.max(0, height - padding);
    const lines = Math.max(1, Math.floor(available / lineHeight));

    return this._getHeightByLines(lines);
  }

  _getCollapsedHeight() {
    if (this._itemsLimit > 0) {
      const items = this._getItems();
      const lastIndex = Math.min(this._itemsLimit, items.length) - 1;
      const lastVisible = items[lastIndex];

      if (!lastVisible) {
        return 0;
      }

      return this._getHeightToItem(lastVisible);
    }

    if (this._linesLimit > 0) {
      return this._getHeightByLines(this._linesLimit);
    }

    if (this._heightLimit > 0) {
      return this._snapHeightToLines(this._heightLimit);
    }

    return 0;
  }

  _getFullHeight() {
    const items = this._getItems();
    const lastItem = items[items.length - 1];

    if (lastItem) {
      return this._getHeightToItem(lastItem);
    }

    return this._content.scrollHeight;
  }

  _setMaxHeight(value, animate) {
    if (!animate) {
      this._content.style.transition = 'none';
    }

    this._content.style.maxHeight = `${value}px`;

    if (!animate) {
      void this._content.offsetHeight;
      this._content.style.transition = 'max-height 0.4s ease';
    }
  }

  _applyHeight(animate) {
    this._collapsedHeight = this._getCollapsedHeight();
    const fullHeight = this._getFullHeight();
    const target = this._isOpen ? fullHeight : this._collapsedHeight;

    if (target <= 0) {
      return;
    }

    if (animate && !this._isOpen) {
      const current = this._content.style.maxHeight;
      if (!current) {
        this._setMaxHeight(fullHeight, false);
      }
    }

    this._setMaxHeight(target, animate);
  }

  _startPinning() {
    this._stopPinning();
    this._anchorTop = this._content.getBoundingClientRect().top;
    this._isPinning = true;
    this._content.addEventListener('transitionend', this._onTransitionEnd);
    this._pinTick();
  }

  _stopPinning() {
    this._isPinning = false;

    if (this._content) {
      this._content.removeEventListener('transitionend', this._onTransitionEnd);
    }
  }

  _pinTick() {
    if (!this._isPinning) {
      return;
    }

    const delta = this._content.getBoundingClientRect().top - this._anchorTop;

    if (delta !== 0) {
      window.scrollBy(0, delta);
    }

    requestAnimationFrame(() => {
      this._pinTick();
    });
  }

  _onTransitionEnd(evt) {
    if (evt.target !== this._content || evt.propertyName !== 'max-height') {
      return;
    }

    this._stopPinning();

    const delta = this._content.getBoundingClientRect().top - this._anchorTop;

    if (delta !== 0) {
      window.scrollBy(0, delta);
    }
  }

  _hasOverflow() {
    const items = this._getItems();

    if (this._itemsLimit > 0) {
      return items.length > this._itemsLimit;
    }

    if (this._linesLimit > 0) {
      return this._getFullHeight() > this._getHeightByLines(this._linesLimit);
    }

    if (this._heightLimit > 0) {
      return this._getFullHeight() > this._snapHeightToLines(this._heightLimit);
    }

    return false;
  }

  _applyState(animate) {
    if (!this._hasOverflow()) {
      this._content.style.maxHeight = '';
      this._content.classList.remove('is-open');
      this._btn.style.display = 'none';
      this._btn.classList.remove('is-active');
      this._btnText.textContent = this._originalText;
      this._isOpen = false;
      return;
    }

    this._btn.style.display = '';
    this._content.style.overflow = 'hidden';

    if (animate) {
      this._startPinning();
    }

    this._applyHeight(animate);

    this._btnText.textContent = this._isOpen ? 'Свернуть' : this._originalText;
    this._btn.classList.toggle('is-active', this._isOpen);
    this._content.classList.toggle('is-open', this._isOpen);
  }

  _update(animate) {
    if (this._shouldDestroy()) {
      if (!this._isDestroyed) {
        this._isDestroyed = true;
        this._reset();
        this._btn.style.display = 'none';
      }
      return;
    }

    this._isDestroyed = false;
    this._applyState(animate);
  }

  _onClick(evt) {
    evt.preventDefault();

    if (this._isDestroyed) {
      return;
    }

    this.toggle();
  }

  _onResize() {
    requestAnimationFrame(() => {
      this._update(false);
    });
  }

  _onLoad() {
    this._update(false);
  }
}
